(() => {
  const selector = 'xo-sticky[data-foldinglink-stable-scroll]';
  const controllers = new WeakMap();

  const clampThreshold = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? Math.min(96, Math.max(8, parsed)) : 24;
  };

  const createController = (sticky) => {
    if (controllers.has(sticky)) return controllers.get(sticky);

    const abortController = new AbortController();
    const { signal } = abortController;
    const threshold = clampThreshold(sticky.dataset.foldinglinkScrollThreshold);
    let frame = 0;
    let readyTimer = 0;
    let inner = null;
    let headerHeight = threshold;
    let lastScrollY = Math.max(0, window.scrollY);
    let directionAnchorY = lastScrollY;
    let direction = 0;
    let isVisible = lastScrollY <= threshold;

    const measure = () => {
      inner = sticky.shadowRoot?.querySelector('xo-sticky-inner') || null;
      headerHeight = Math.max(inner?.offsetHeight || sticky.offsetHeight || threshold, threshold);
    };

    const render = () => {
      frame = 0;

      if (!sticky.isConnected) {
        abortController.abort();
        return;
      }

      const currentScrollY = Math.max(0, window.scrollY);
      const delta = currentScrollY - lastScrollY;

      if (currentScrollY <= headerHeight) {
        isVisible = true;
        direction = 0;
        directionAnchorY = currentScrollY;
        inner?.style.removeProperty('transform');
        sticky.dataset.foldinglinkHeaderState = 'top';
        lastScrollY = currentScrollY;
        return;
      }

      const nextDirection = Math.abs(delta) >= 0.5 ? Math.sign(delta) : direction;

      if (nextDirection !== 0 && nextDirection !== direction) {
        direction = nextDirection;
        directionAnchorY = lastScrollY;
      }

      if (direction > 0 && currentScrollY - directionAnchorY >= threshold) {
        isVisible = false;
        directionAnchorY = currentScrollY;
      } else if (direction < 0 && directionAnchorY - currentScrollY >= threshold) {
        isVisible = true;
        directionAnchorY = currentScrollY;
      }

      if (inner) {
        inner.style.transform = isVisible
          ? 'translate3d(0, 0, 0)'
          : `translate3d(0, -${headerHeight}px, 0)`;
      }

      sticky.dataset.foldinglinkHeaderState = isVisible ? 'visible' : 'hidden';
      lastScrollY = currentScrollY;
    };

    const scheduleRender = () => {
      if (frame !== 0) return;

      frame = window.requestAnimationFrame(() => {
        frame = window.requestAnimationFrame(render);
      });
    };

    const handleResize = () => {
      measure();
      scheduleRender();
    };

    const destroy = () => {
      abortController.abort();
      if (frame !== 0) window.cancelAnimationFrame(frame);
      if (readyTimer !== 0) window.clearTimeout(readyTimer);
      inner?.style.removeProperty('transform');
      delete sticky.dataset.foldinglinkHeaderState;
      controllers.delete(sticky);
    };

    window.addEventListener('scroll', scheduleRender, { passive: true, signal });
    window.addEventListener('resize', handleResize, { passive: true, signal });

    measure();
    scheduleRender();
    readyTimer = window.setTimeout(() => {
      measure();
      scheduleRender();
    }, 150);

    const controller = { destroy };
    controllers.set(sticky, controller);
    return controller;
  };

  const initializeWithin = (scope = document) => {
    if (scope.matches?.(selector)) createController(scope);
    scope.querySelectorAll?.(selector).forEach(createController);
  };

  const destroyWithin = (scope) => {
    if (scope.matches?.(selector)) controllers.get(scope)?.destroy();
    scope.querySelectorAll?.(selector).forEach((sticky) => controllers.get(sticky)?.destroy());
  };

  const initialize = () => initializeWithin(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
